import {
  ISenamhiRepository,
  NowcastingReportEntity,
  WarningListEntity,
  WarningNarrativeEntity,
  ResponseValueObject,
  IConfiguration,
} from "@gishub/domain";
import { axiosClient } from "../http/axios-client";

/**
 * Repositorio SENAMHI (nowcasting + avisos).
 *
 * La narrativa oficial de cada aviso ("se esperan acumulados de
 * precipitación…") no llega en el JSON de la lista: vive en la página
 * de detalle `?p=aviso-meteorologico-detalle&a=<año>&b=<nro><mapa>&c=00&d=SENA`
 * de senamhi.gob.pe. Este repositorio la scrapea a demanda (con caché,
 * vía el proxy de la plataforma y con degradación elegante).
 */
export class SenamhiRepository implements ISenamhiRepository {
  private readonly nowcastingEndpoint = "/scraping/senamhi/nowcasting/report";
  private readonly warningsEndpoint = "/scraping/senamhi/warning/list";

  /** URL de la página oficial de detalle de avisos. */
  private static readonly DETAIL_URL =
    "https://www.senamhi.gob.pe/?p=aviso-meteorologico-detalle";

  /** Caché de narrativas por clave `${code}_${map}_${year}`. */
  private static readonly narrativeCache = new Map<
    string,
    Promise<ResponseValueObject<WarningNarrativeEntity | null>>
  >();

  constructor(private readonly configuration?: IConfiguration) {}

  /** URL del proxy de la plataforma (`…/proxy?<url>`), si está disponible. */
  private get proxyBase(): string | null {
    return this.configuration?.proxyUrl?.replace(/\/+$/, "") ?? null;
  }

  private async get<T>(url: string, timeoutMs = 15_000): Promise<T> {
    const response = await axiosClient.get<T>(url, { timeout: timeoutMs });
    return response.data;
  }

  /** GET HTML crudo por el proxy de la plataforma (evita CORS). */
  private async fetchHtml(url: string, timeoutMs = 15_000): Promise<string> {
    const proxy = this.proxyBase;
    if (proxy) {
      const res = await fetch(`${proxy}?${url}`);
      if (res.ok) return res.text();
    }
    // Sin configuración de proxy: intento directo (p. ej. tests).
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.text();
  }

  async getNowcastingReport(): Promise<
    ResponseValueObject<NowcastingReportEntity>
  > {
    try {
      const payload = await this.get<
        ResponseValueObject<NowcastingReportEntity>
      >(this.nowcastingEndpoint);

      return new ResponseValueObject(
        payload.success,
        payload.data,
        payload.message,
      );
    } catch (err: any) {
      return new ResponseValueObject<NowcastingReportEntity>(
        false,
        undefined,
        err?.message ?? "Error al obtener reporte de nowcasting SENAMHI",
      );
    }
  }

  async getWarningsList(): Promise<ResponseValueObject<WarningListEntity>> {
    try {
      const payload = await this.get<ResponseValueObject<WarningListEntity>>(
        this.warningsEndpoint,
      );

      return new ResponseValueObject(
        payload.success,
        payload.data,
        payload.message,
      );
    } catch (err: any) {
      return new ResponseValueObject<WarningListEntity>(
        false,
        undefined,
        err?.message ?? "Error al obtener lista de avisos SENAMHI",
      );
    }
  }

  async getWarningNarrative(
    code: number,
    mapNumber: number,
    year: number,
  ): Promise<ResponseValueObject<WarningNarrativeEntity | null>> {
    const key = `${code}_${mapNumber}_${year}`;
    const cached = SenamhiRepository.narrativeCache.get(key);
    if (cached) return cached;

    const promise = this.fetchNarrative(code, mapNumber, year);
    SenamhiRepository.narrativeCache.set(key, promise);
    return promise;
  }

  private async fetchNarrative(
    code: number,
    mapNumber: number,
    year: number,
  ): Promise<ResponseValueObject<WarningNarrativeEntity | null>> {
    try {
      const pageUrl = `${SenamhiRepository.DETAIL_URL}&a=${year}&b=${code}${mapNumber}&c=00&d=SENA`;
      const html = await this.fetchHtml(pageUrl);

      // La página repite la lista completa; el panel del aviso se ancla
      // en su encabezado "N°<code>" y la narrativa son los <p justify>
      // siguientes (uno por día de vigencia).
      const clean = html
        .replace(/<script[\s\S]*?<\/script>/gi, " ")
        .replace(/<style[\s\S]*?<\/style>/gi, " ");
      const anchor = new RegExp(
        "N(?:\\s|&nbsp;|&deg;|&ordm;|\u00b0)*" +
          code +
          "\\b[\\s\\S]{0,220}?<h1[^>]*>([\\s\\S]{0,300}?)</h1>",
        "i",
      );
      const m = clean.match(anchor);
      if (!m) {
        return new ResponseValueObject(true, null);
      }

      const start = m.index ?? 0;
      const window = clean.slice(start, start + 8_000);
      const paragraphs = [...window.matchAll(JUSTIFIED_PARAGRAPHS)]
        .map((x) => decodeEntities(x[1]))
        .filter((t) => t.length > 25);
      if (paragraphs.length === 0) {
        return new ResponseValueObject(true, null);
      }

      const title = decodeEntities(m[1]);
      return new ResponseValueObject(true, { title, paragraphs });
    } catch {
      // Degradación elegante: sin narrativa la UI muestra el fallback.
      return new ResponseValueObject(true, null);
    }
  }
}

/** Decodifica las entidades HTML que usa la página de SENAMHI. */
function decodeEntities(input: string): string {
  const ta = document.createElement("textarea");
  ta.innerHTML = input;
  return ta.textContent?.replace(/\s+/g, " ").trim() ?? "";
}

const JUSTIFIED_PARAGRAPHS =
  /<p[^>]*align=["']?justify["']?[^>]*>([\s\S]*?)<\/p>/gi;
